import {Component, ElementRef, Input, OnChanges, OnInit} from '@angular/core';
import * as d3 from "d3";

export interface ChartData {
  uId: string;
  value: number;
  period: string;
  isProjected: boolean;
  valueKeyName: string;
}

@Component({
  selector: 'app-line-projection-chart',
  templateUrl: './line-projection-chart.component.html',
  styleUrls: ['./line-projection-chart.component.scss']
})
export class LineProjectionChartComponent implements OnInit, OnChanges {
  @Input() chartData!: ChartData[];
  @Input() showLoader: boolean = false;
  @Input() bindValueKeyName: string = 'value';
  @Input() lineLabelName: string = '';

  public xScale: any;
  public yScale: any;
  public chart: any;
  public data: ChartData[] = [];
  // Set up chart dimensions
  public svgWidth: number = 750;
  public svgHeight: number = 340;
  public margin = {top: 40, right: 30, bottom: 80, left: 60};
  public width = this.svgWidth - this.margin.left - this.margin.right;
  public height = this.svgHeight - this.margin.top - this.margin.bottom;
  public gap = 12;
  public legends = {
    line: {
      label: this.lineLabelName || 'Metrics',
      color: '#71b06b',
    },
    projection: {
      label: 'Projection',
      color: '#ffffff',
    },
  };

  constructor(
    private elementRef: ElementRef) {
  }

  ngOnChanges(changes: any) {
    if (changes.showLoader?.currentValue) {
      this.showLoader = changes.showLoader.currentValue
    }
    if (changes.chartData?.currentValue) {
      d3.select(this.elementRef.nativeElement)
        .select('#line-projection-chart').select('*').remove();
      this.data = changes.chartData.currentValue;
      this.initLineProjectionChart();
    }
  }

  ngOnInit(): void {

  }

  initLineProjectionChart(): void {
    // Setting up svg view-box
    const svg = d3
      .select(this.elementRef.nativeElement)
      .select('#line-projection-chart')
      .attr('viewBox', `0,0, ${this.svgWidth},${this.svgHeight}`);

    // Chart reference
    this.chart = svg
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    // X scale
    this.xScale = d3
      .scaleBand()
      .domain(this.data.map((d) => d.period))
      .range([0, this.width])
      .padding(0.5);

    // Y scale
    this.yScale = d3
      .scaleLinear()
      .domain([0, d3.max(this.data, (d: any) => Math.max(d[this.bindValueKeyName])) || 1000])
      .range([this.height, 0]);

    // Create bottom x-axis
    const xAxis = d3.axisBottom(this.xScale)

    // Add x-axis
    this.chart
      .append('g')
      .attr('class', 'x-axis')
      .attr('color', 'rgba(255, 255, 255, 0.2)')
      .attr('transform', `translate(0,${this.height})`)
      .call(xAxis)

    // Add grid lines
    this.chart
      .append('g')
      .attr('class', 'gridlines')
      .attr('color', 'rgba(255, 255, 255, 0.1)')
      .call(d3.axisLeft(this.yScale).tickSize(-this.width))

    // Set legends
    this.setLegends();

    // Set vertical projection line
    this.drawVerticalProjectionLine();

    // Add solid line path
    this.drawLineChart({
      data: this.data.filter((d) => !d.isProjected),
      isDotted: false,
      lineColor: this.legends.line.color,
    });

    // Add dotted line path
    this.drawLineChart({
      data: this.data,
      isDotted: true,
      lineColor: this.legends.line.color,
    });

    // Set line path circle dots
    this.setLinePathDots(this.bindValueKeyName, this.legends.line.color);

    // set line path labels above circle dots
    this.setLinePathLabels(this.bindValueKeyName);
  }

  // Add Legends
  setLegends() {
    const legend = this.chart
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(0, ${this.height + this.margin.top} )`);
    let xOffset = 0;
    const generateLegends = (color: string, text: string) => {
      const legendRef = legend.append('g').attr('transform', `translate(${xOffset}, 0)`);
      legendRef.append('circle').attr('r', 5).attr('fill', color).attr('cy', -5);
      const textElement = legendRef.append('text').text(text).attr('x', 10);
      xOffset += textElement.node().getBBox().width + 30;
    };
    Object.entries(this.legends).forEach(([key, value]) => {
      if (key === 'line') {
        value.label = this.lineLabelName || value.label;
      }
      generateLegends(value.color, value.label)
    });
  }

  // Set vertical projection line
  drawVerticalProjectionLine() {
    const data = this.data.filter((d) => !d.isProjected);
    const projection = this.chart
      .append('g')
      .attr('class', 'projection-divider')
      .attr(
        'transform',
        `translate(${this.xScale(data[data.length - 1].period) + this.gap}, 0)`
      );
    projection
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', this.height + this.margin.bottom + this.margin.top)
      .attr('stroke', '#FFF')
      .attr('stroke-width', 1);
    projection
      .append('circle')
      .attr('r', 5)
      .attr('fill', '#FFF')
      .attr('cx', 0)
      .attr('cy', 0);
    projection
      .append('circle')
      .attr('r', 5)
      .attr('fill', '#FFF')
      .attr('cx', 0)
      .attr('cy', this.height + this.margin.bottom + this.margin.top - 45);
  }

  // Add line path (Solid/Dotted)
  drawLineChart(
    options: {
      data: ChartData[],
      isDotted: boolean
      lineColor: string
    }) {

    // Create line generator
    const generateLine = d3
      .line<ChartData>()
      .x((d: any) => this.xScale(d.period) || 0)
      .y((d: any) => this.yScale(d[this.bindValueKeyName]) || 0);

    // Add line path
    this.chart
      .append('path')
      .datum(options.data)
      .attr('fill', 'none')
      .attr('transform', `translate(${this.gap}, 0)`)
      .attr('stroke', options.lineColor)
      .attr('stroke-linejoin', 'round')
      .attr('stroke-linecap', 'round')
      .attr('stroke-dasharray', options.isDotted ? '3,3' : '0')
      .attr('stroke-width', options.isDotted ? 1 : 1.5)
      .attr('d', generateLine)
  }

  // Set line path circle dots
  setLinePathDots(
    key: string,
    fill: string = '#FFF'
  ) {
    this.chart
      .selectAll('.circle')
      .data(this.data)
      .enter()
      .append('circle')
      .attr('class', 'dot')
      .attr('transform', `translate(${this.gap}, 0)`)
      .attr('cx', (d: any) => this.xScale(d.period) || 0)
      .attr('cy', (d: any) => this.yScale(d[key]) || 0)
      .attr('r', 3)
      .style('fill', fill);
  }

  // set line path labels
  setLinePathLabels(
    keyname: string,
    color: string = '#cbcbd2'
  ) {
    this.chart
      .selectAll('.label')
      .data(this.data)
      .enter()
      .append('text')
      .attr('x', (d: any) => this.xScale(d.period) || 0)
      .attr('y', (d: any) => this.yScale(d[keyname]) || 0)
      .attr('transform', `translate(${this.gap}, 0)`)
      .attr('dx', (d: any) => d[keyname].toString().length * -3)
      .attr('dy', '-6')
      .attr('font-size', '12px')
      .text((d: any) => d[keyname])
      .style('fill', color);
  }
}
